import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseEntityRef } from '@backstage/catalog-model';
import { useSidebarPinState } from '@backstage/core-components';
import { useTechDocsReaderPage } from '@backstage/plugin-techdocs-react';
import { DocsNavPill } from './docsNav/DocsNavPill';
import { DocsNavSheet } from './docsNav/DocsNavSheet';
import { docsNavSubtitle } from './docsNav/docsNavSubtitle';
import type { DocsNavItem, DocsNavTab } from './docsNav/types';
import { useReaderNav } from './useReaderNav';
import { useReaderToc } from './useReaderToc';

/*
 * The phone reader's navigation (mocks 3c and 3d).
 *
 * There is no room for either of the reader's rails at 402px, so both are hidden
 * and the state behind them floats over the prose instead: the split pill above
 * the bottom tab bar, and the sheet it opens. `useReaderNav` supplies the doc
 * set's page tree — the left rail — and `useReaderToc` the current page's
 * headings, the same hook the desktop rail draws from.
 *
 * It renders from the Subheader location, which is app DOM rather than the shadow
 * root: a `position: fixed` box in the shadow tree would depend on the reader's
 * own ancestors never becoming a containing block, and the pill has to clear the
 * reader's overflow either way.
 */
export const DocsNavAddon = () => {
  const { isMobile } = useSidebarPinState();
  const navigate = useNavigate();
  const { metadata, entityMetadata, entityRef } = useTechDocsReaderPage();
  const { headings, activeId, activeIndex, jumpTo } = useReaderToc();
  const { entries, title, pageCount, select } = useReaderNav();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<DocsNavTab>('contents');

  if (!isMobile || (entries.length === 0 && headings.length === 0)) return null;

  /*
   * The nav prints the doc set's name above its tree, which is the same
   * `site_name` the byline shows. The entity's own title and then its name stand
   * in while the tree and the metadata are still loading, as they do there.
   */
  const docSet =
    title ??
    metadata.value?.site_name ??
    entityMetadata.value?.metadata.title ??
    entityRef.name;

  const ownerRef = entityMetadata.value?.relations?.find(
    relation => relation.type === 'ownedBy',
  )?.targetRef;

  const contents: DocsNavItem[] = entries.map(entry => ({
    id: `page-${entry.index}`,
    label: entry.label,
    depth: entry.depth,
    active: entry.active,
    isSection: entry.isSection,
    onSelect: () => select(entry.index),
  }));

  const tocItems: DocsNavItem[] = headings.map(heading => ({
    id: heading.id,
    label: heading.label,
    depth: heading.depth,
    active: heading.id === activeId,
    onSelect: () => jumpTo(heading.id),
  }));

  const openOn = (next: DocsNavTab) => {
    setTab(next);
    setOpen(true);
  };

  return (
    <>
      <DocsNavPill
        onThisPageLabel={
          headings.length > 0
            ? `On this page · ${activeIndex + 1}/${headings.length}`
            : undefined
        }
        hasContents={contents.length > 0}
        onOpenContents={() => openOn('contents')}
        onOpenToc={() => openOn('onThisPage')}
      />
      <DocsNavSheet
        open={open}
        title={docSet}
        subtitle={docsNavSubtitle(
          ownerRef ? parseEntityRef(ownerRef).name : undefined,
          pageCount,
          'page',
        )}
        tab={tab}
        onTabChange={setTab}
        contents={contents}
        headings={tocItems}
        onClose={() => setOpen(false)}
        onAllDocs={() => {
          setOpen(false);
          navigate('/documentation');
        }}
      />
    </>
  );
};
